/**
 * UC3 Relationships step (client, 11 Oct 2026, Part B): Local Bivariate Relationships
 * between the crop's production grid (GGCP10, about 10 km) and one factor, run as the
 * `local_bivariate` analysis job and polled like every other tool (`useToolRun`).
 *
 * The runs live in `useUc3Relationships` (`useUc3Relationships.ts`), called by `Uc3` itself, not in the step body: the
 * stepper renders only the active step, and a run (or a job still polling) must survive the
 * reader moving to another step. Each finished run adds one flow layer (`intel.uc3Layers`,
 * step `relationships`, legend checkbox per run, named "Productivity vs vegetation, Burkina
 * Faso, 2020") and one result card `#uc3LbrResult-<n>`; up to three runs are kept and a
 * fourth replaces the oldest with a note. Reset closes the use case, which unmounts `Uc3`
 * and clears the flow, so the runs go with it.
 *
 * One step since 12 Oct 2026 (NOTES #759): the Vegetation, Rainfall and Soil steps are gone
 * and their choices live here. The factor (`#uc3LbrFactor`), the soil variable (`#uc3Soil`,
 * shown for Soil) and the opacity of the factor's layer (`#uc3Opacity`) are `Uc3`'s state,
 * because `Uc3` paints the factor's raster on the map (flow step `factor`) as soon as it is
 * chosen, before any run, so the reader sees what production is compared with. Runs
 * accumulate in any order; `#uc3LbrCompare` ("Compare") shows from the first run: one run's
 * share bar alone, two or three side by side. The Water use (WaPOR AETI) toggle the Rainfall
 * step carried is not here: it only swapped the map layer, and no Relationships factor or
 * result reads it.
 *
 * Results stage (client, 12 Oct 2026, NOTES #760): the Analyse step is gone and the runs are
 * read at Results. This step keeps the controls, Run and the progress bar; a finished run
 * moves the reader to Results, where `Uc3RelationshipsResults` draws the compare view and
 * each run's card. The classes are named in plain words (`lbrClasses.ts`: "Higher together",
 * with a hover sentence naming the factor and the method's term on a second line); the
 * method's term stays as a small secondary column of each card's table.
 *
 * Words: rank and co-occurrence only. The sentence on each card is the server's, in both
 * languages, in the same register as the labels; nothing here says one variable acts on the
 * other.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartFrame } from '../../components/charts/ChartFrame';
import { BarsH } from '../../components/charts/BarsH';
import {
  LBR_CLASS_COLORS,
  TIMED_OUT,
  type LbrFactor,
  type LocalBivariateResult,
} from '../../lib/analysis';
import { localisedUnit } from '../../lib/display';
import { fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { ProgressBar } from '../analysis/ProgressBar';
import { StackedBar } from './charts/StackedBar';
import { LBR_LEGEND_ORDER, lbrHint, lbrLabel, lbrMethodTerm, lbrSubject } from './lbrClasses';
import {
  LBR_CROPS,
  LBR_DEFAULT_K,
  LBR_DEFAULT_PERMUTATIONS,
  LBR_FACTORS,
  LBR_MAX_PERMUTATIONS,
  lbrRunName,
  type LbrRun,
  type Uc3RelationshipsState,
} from './useUc3Relationships';

/** The soil variables the Soil factor offers (`soc`, `ph`, `sgcec`, `sgn`). */
export const LBR_SOIL_LAYERS = ['soc', 'ph', 'sgcec', 'sgn'] as const;
export type LbrSoilLayer = (typeof LBR_SOIL_LAYERS)[number];

interface StepProps {
  state: Uc3RelationshipsState;
  unitId: string | null;
  crop: string;
  cropLabel: string;
  year: number | null;
  factor: LbrFactor;
  onFactor: (factor: LbrFactor) => void;
  soilLayer: LbrSoilLayer;
  onSoil: (layer: LbrSoilLayer) => void;
  /** The factor layer's opacity, 0.1 to 1, and its setter. */
  opacity: number;
  onOpacity: (value: number) => void;
  /** "Vegetation is for the 2021 season, the earliest available." when the rule moved it. */
  seasonNote?: string | null;
  /** Which rainfall the Rainfall factor reads (season dekads or the annual total). */
  rainNote?: string | null;
  method?: { steps: string[]; limitations?: string };
}

export function Uc3RelationshipsStep({
  state,
  unitId,
  crop,
  cropLabel,
  year,
  factor,
  onFactor,
  soilLayer,
  onSoil,
  opacity,
  onOpacity,
  seasonNote,
  rainNote,
  method,
}: StepProps) {
  const { t } = useTranslation();
  const [k, setK] = useState(LBR_DEFAULT_K);
  const [permutations, setPermutations] = useState(LBR_DEFAULT_PERMUTATIONS);
  const [fdr, setFdr] = useState(false);
  const { run } = state;

  const hasGrid = (LBR_CROPS as readonly string[]).includes(crop);
  const blocked = !hasGrid ? t('intel.uc3.lbr.unavailable', { crop: cropLabel }) : !unitId ? t('intel.uc3.lbr.needUnit') : null;
  const message = run.error === TIMED_OUT ? t('analysis.timedOut') : run.error;

  const submit = () => {
    if (blocked || year === null || !unitId) return;
    state.submit({
      unit_id: unitId,
      crop,
      year,
      factor,
      ...(factor === 'soil' ? { soil_var: soilLayer } : {}),
      k,
      permutations,
      fdr,
    });
  };

  return (
    <div className="ad-checks uc3-lbr">
      {blocked ? (
        <p className="note" id="uc3LbrUnavailable">
          {blocked}
        </p>
      ) : null}
      <label>
        {t('intel.uc3.lbr.factorLabel')}
        <select
          id="uc3LbrFactor"
          value={factor}
          disabled={!hasGrid}
          onChange={(e) => onFactor(e.target.value as LbrFactor)}
        >
          {LBR_FACTORS.map((f) => (
            <option key={f} value={f}>
              {t(`intel.uc3.lbr.factor.${f}`)}
            </option>
          ))}
        </select>
      </label>
      {factor === 'soil' ? (
        <label>
          {t('intel.uc3.lbr.soilLabel')}
          <select id="uc3Soil" value={soilLayer} onChange={(e) => onSoil(e.target.value as LbrSoilLayer)}>
            {LBR_SOIL_LAYERS.map((l) => (
              <option key={l} value={l}>
                {t(`intel.uc3.layer.${l}`)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {factor === 'rain' && rainNote ? (
        <p className="note" id="uc3LbrRainNote">
          {rainNote}
        </p>
      ) : null}
      {factor === 'ndvi' && seasonNote ? (
        <p className="note" id="uc3LbrSeasonNote">
          {seasonNote}
        </p>
      ) : null}
      <label>
        {t('intel.uc3.opacityLabel')}
        <input
          id="uc3Opacity"
          type="range"
          min={0.1}
          max={1}
          step={0.05}
          value={opacity}
          onChange={(e) => onOpacity(Number(e.target.value))}
        />
      </label>
      <details id="uc3LbrAdvanced">
        <summary>{t('intel.uc3.lbr.advanced')}</summary>
        <label>
          {t('intel.uc3.lbr.k')}
          <input
            id="uc3LbrK"
            type="number"
            min={12}
            max={100}
            step={1}
            value={k}
            onChange={(e) => setK(clampInt(e.target.value, 12, 100, LBR_DEFAULT_K))}
          />
        </label>
        <label>
          {t('intel.uc3.lbr.permutations')}
          <input
            id="uc3LbrPermutations"
            type="number"
            min={19}
            max={LBR_MAX_PERMUTATIONS}
            step={1}
            value={permutations}
            onChange={(e) =>
              setPermutations(clampInt(e.target.value, 19, LBR_MAX_PERMUTATIONS, LBR_DEFAULT_PERMUTATIONS))
            }
          />
        </label>
        <label>
          <input id="uc3LbrFdr" type="checkbox" checked={fdr} onChange={(e) => setFdr(e.target.checked)} />
          {t('intel.uc3.lbr.fdr')}
        </label>
      </details>
      <button
        type="button"
        className="btn primary ad-run"
        id="uc3LbrRun"
        disabled={run.phase === 'running' || Boolean(blocked) || year === null}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('intel.uc3.lbr.run')}
      </button>
      {run.phase === 'running' ? <ProgressBar value={run.progress} label={t('analysis.progress')} /> : null}
      {run.phase === 'error' ? (
        <div className="note" role="alert" id="uc3LbrError">
          {message ?? t('analysis.failed')}
        </div>
      ) : null}

      {method?.steps.length ? (
        <details id="uc3LbrMethod">
          <summary>{t('intel.uc3.lbr.method')}</summary>
          <ol className="uc1-steps">
            {method.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
          {method.limitations ? <p className="sub">{method.limitations}</p> : null}
        </details>
      ) : null}
    </div>
  );
}

function clampInt(raw: string, low: number, high: number, fallback: number): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(high, Math.max(low, n));
}

function runName(t: (k: string, o?: Record<string, unknown>) => string, result: LocalBivariateResult): string {
  return lbrRunName(t, result);
}

/**
 * The Results stage's Relationships block (NOTES #760): the compare view (one to three runs,
 * share bars side by side, the sentences), then each run's card with its stats table; a
 * plain line while no run exists yet.
 */
export function Uc3RelationshipsResults({ state, lang }: { state: Uc3RelationshipsState; lang: 'en' | 'fr' }) {
  const { t } = useTranslation();
  const { runs, replaced } = state;
  if (!runs.length) {
    return (
      <p className="note" id="uc3LbrNoRuns">
        {t('intel.uc3.lbr.noRuns')}
      </p>
    );
  }
  return (
    <div className="uc3-lbr-results" id="uc3LbrResults">
      {replaced ? (
        <p className="note" id="uc3LbrReplaced">
          {t('intel.uc3.lbr.replaced', replaced)}
        </p>
      ) : null}
      <LbrCompare runs={runs} lang={lang} />
      {runs.map((r) => (
        <LbrResultCard key={r.n} run={r} lang={lang} />
      ))}
    </div>
  );
}

function LbrResultCard({ run, lang }: { run: LbrRun; lang: 'en' | 'fr' }) {
  const { t } = useTranslation();
  const { result, n } = run;
  const name = runName(t, result);
  const rows = LBR_LEGEND_ORDER.map((key) => result.classes.find((c) => c.key === key)).filter(
    (c): c is NonNullable<typeof c> => Boolean(c),
  );
  const subject = lbrSubject(result.factor, result.soil_var);
  const factorWord = t(`intel.uc3.lbr.factorWord.${result.factor}`);
  const yDecimals = result.factor === 'ndvi' ? 2 : 1;
  return (
    <div className="result-box" id={`uc3LbrResult-${n}`} data-factor={result.factor}>
      <h5>{t('intel.uc3.lbr.resultTitle', { n, name })}</h5>
      <div className="result-pad">
        <p className="sub">{result.unit.name}</p>
        <ul className="chart-legend uc3-lbr-classes" aria-label={t('intel.uc3.lbr.classesTitle')}>
          {rows.map((c) => (
            <li key={c.key} data-lbr-class={c.key} title={lbrHint(t, c.key, subject)}>
              <span className="swatch" style={{ background: LBR_CLASS_COLORS[c.key] }} />
              <span className="legend-label">{lbrLabel(t, c.key)}</span>
            </li>
          ))}
        </ul>
        <ChartFrame
          title={t('intel.uc3.lbr.sharesTitle')}
          subtitle={name}
          xLabel={t('intel.uc3.lbr.axisShare')}
          yLabel={t('intel.uc3.lbr.axisClass')}
          testId={`uc3-lbr-shares-${n}`}
        >
          {() => (
            <BarsH
              labels={rows.map((c) => lbrLabel(t, c.key))}
              rowTitles={rows.map((c) => lbrHint(t, c.key, subject))}
              series={[
                {
                  key: 'share',
                  label: t('intel.uc3.lbr.axisShare'),
                  color: LBR_CLASS_COLORS.positive_linear,
                  values: rows.map((c) => c.share),
                  rowColors: rows.map((c) => LBR_CLASS_COLORS[c.key]),
                },
              ]}
              unit="%"
              fmt={0}
              ariaLabel={t('intel.uc3.lbr.sharesTitle')}
            />
          )}
        </ChartFrame>
        <p className="uc3-lbr-sentence" data-testid={`uc3-lbr-sentence-${n}`}>
          {result.sentence[lang]}
        </p>
        <table className="mini">
          <thead>
            <tr>
              <th>{t('intel.uc3.lbr.table.class')}</th>
              <th className="uc3-lbr-method">{t('intel.uc3.lbr.table.method')}</th>
              <th className="num">{t('intel.uc3.lbr.table.cells')}</th>
              <th className="num">{t('intel.uc3.lbr.table.share')}</th>
              <th className="num">{t('intel.uc3.lbr.table.meanX', { unit: localisedUnit(result.x_unit, lang) })}</th>
              <th className="num">
                {t('intel.uc3.lbr.table.meanY', { factor: factorWord, unit: localisedUnit(result.y_unit, lang) })}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.key} data-lbr-row={c.key} title={lbrHint(t, c.key, subject)}>
                <td>{lbrLabel(t, c.key)}</td>
                <td className="uc3-lbr-method">{lbrMethodTerm(t, c.key)}</td>
                <td className="num">{fmtNum(c.cells, 0)}</td>
                <td className="num">{`${fmtNum(c.share, 0)} %`}</td>
                <td className="num">{fmtNum(c.mean_x, 2)}</td>
                <td className="num">{fmtNum(c.mean_y, yDecimals)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="tbl-note">
          {t('intel.uc3.lbr.runFacts', {
            cells: fmtNum(result.cells_analysed, 0),
            skipped: fmtNum(result.cells_skipped, 0),
            k: result.k,
            permutations: result.permutations,
            xYear: result.x_year,
          })}{' '}
          <span data-testid={`uc3-lbr-fdr-${n}`}>
            {t(result.fdr ? 'intel.uc3.lbr.fdrApplied' : 'intel.uc3.lbr.fdrNotApplied')}
          </span>
        </p>
        {result.x_year_note ? (
          <p className="note" data-testid={`uc3-lbr-xnote-${n}`}>
            {result.x_year_note[lang]}
          </p>
        ) : null}
        {result.y_year_note ? (
          <p className="note" data-testid={`uc3-lbr-ynote-${n}`}>
            {result.y_year_note[lang]}
          </p>
        ) : null}
        <details id={`uc3LbrLimits-${n}`}>
          <summary>{t('intel.uc3.lbr.limitations')}</summary>
          <ul className="tbl-note">
            {result.limitations.map((l, i) => (
              <li key={i}>{l[lang]}</li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}

function LbrCompare({ runs, lang }: { runs: LbrRun[]; lang: 'en' | 'fr' }) {
  const { t } = useTranslation();
  return (
    <div className="result-box" id="uc3LbrCompare">
      <h5>{t('intel.uc3.lbr.compareTitle')}</h5>
      <div className="result-pad">
        <ChartFrame
          title={t('intel.uc3.lbr.compareTitle')}
          subtitle={t('intel.uc3.lbr.compareSub')}
          xLabel={t('intel.uc3.lbr.compareAxis')}
          yLabel={t('intel.uc3.lbr.compareRows')}
          testId="uc3-lbr-compare"
        >
          {() => (
            <div className="uc3-lbr-compare">
              <div className="uc3-lbr-compare-runs" data-runs={runs.length}>
              {runs.map((r) => {
                const name = runName(t, r.result);
                return (
                  <div key={r.n} className="uc3-lbr-compare-row" data-run={r.n}>
                    <p className="sub">{t('intel.uc3.lbr.resultTitle', { n: r.n, name })}</p>
                    <StackedBar
                      ariaLabel={t('intel.uc3.lbr.resultTitle', { n: r.n, name })}
                      unit="%"
                      segments={LBR_LEGEND_ORDER.map((key) => ({
                        id: key,
                        label: lbrLabel(t, key),
                        value: r.result.classes.find((c) => c.key === key)?.share ?? 0,
                        color: LBR_CLASS_COLORS[key],
                        hint: lbrHint(t, key, lbrSubject(r.result.factor, r.result.soil_var)),
                      }))}
                    />
                  </div>
                );
              })}
              </div>
              <ul className="tbl-note" id="uc3LbrCompareSentences">
                {runs.map((r) => (
                  <li key={r.n}>{r.result.sentence[lang]}</li>
                ))}
              </ul>
            </div>
          )}
        </ChartFrame>
      </div>
    </div>
  );
}
